if (!window.customElements.get('cart-shipping-calculator')) {
  window.customElements.define('cart-shipping-calculator', class extends HTMLElement {
    static BRAZILIAN_STATES = {
      AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará',
      DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão',
      MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais', PA: 'Pará',
      PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro',
      RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima',
      SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins'
    };

    connectedCallback() {
      this.inputElement = this.querySelector('input');
      this.buttonElement = this.querySelector('[data-shipping-calculate]');
      this.resultsElement = this.querySelector('[data-shipping-results]');

      this.abortController = new AbortController();

      this.inputElement.addEventListener('input', this.#applyMask.bind(this), { signal: this.abortController.signal });
      this.inputElement.addEventListener('keydown', this.#onKeydown.bind(this), { signal: this.abortController.signal });
      this.buttonElement.addEventListener('click', this.#calculate.bind(this), { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    get strings() {
      return window.themeVariables.strings;
    }

    #applyMask() {
      const digits = this.inputElement.value.replace(/\D/g, '').slice(0, 8);
      this.inputElement.value = digits.length > 5 ? digits.slice(0, 5) + '-' + digits.slice(5) : digits;
    }

    #onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#calculate();
      }
    }

    async #calculate() {
      const digits = this.inputElement.value.replace(/\D/g, '');

      if (digits.length !== 8) {
        this.#showMessage(this.strings.shippingEstimatorInvalidZip);
        return;
      }

      this.buttonElement.setAttribute('aria-busy', 'true');
      this.#showLoading(this.strings.shippingEstimatorCalculating);

      try {
        const address = await this.#lookupCep(digits);

        if (!address) {
          this.#showMessage(this.strings.shippingEstimatorInvalidZip);
          return;
        }

        const location = `${address.localidade} - ${address.uf}`;
        const province = this.constructor.BRAZILIAN_STATES[address.uf] || address.uf;

        const ratesPromise = this.#fetchRates(digits, province);
        await this.#showLoading(this.strings.shippingEstimatorCalculatingFor.replace('@@', location));

        this.#showRates(await ratesPromise, location);
      } catch (errors) {
        this.#showMessage(this.#extractErrorMessage(errors));
      } finally {
        this.buttonElement.removeAttribute('aria-busy');
      }
    }

    async #lookupCep(digits) {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await response.json();

      return data.erro ? null : data;
    }

    async #fetchRates(zip, province) {
      const params = `shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=Brazil&shipping_address[province]=${encodeURIComponent(province)}`;

      const prepareResponse = await fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });

      if (!prepareResponse.ok) {
        throw await prepareResponse.json();
      }

      for (let attempt = 0; attempt < 30; attempt++) {
        const response = await fetch(`${Shopify.routes.root}cart/async_shipping_rates.json?${params}`);
        const text = await response.text();

        if (text !== 'null') {
          return JSON.parse(text)['shipping_rates'];
        }

        await new Promise((resolve) => setTimeout(resolve, 250));
      }

      throw {};
    }

    #extractErrorMessage(errors) {
      if (errors && typeof errors === 'object') {
        const firstKey = Object.keys(errors)[0];

        if (firstKey) {
          return [].concat(errors[firstKey]).join(', ');
        }
      }

      return this.strings.shippingEstimatorError;
    }

    #formatPrice(rate) {
      const price = parseFloat(rate['price']);

      if (price === 0) {
        return this.strings.shippingEstimatorFree;
      }

      try {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: rate['currency'] || 'BRL' }).format(price);
      } catch (error) {
        return `${rate['currency']} ${rate['price']}`;
      }
    }

    #formatDeliveryDays(rate) {
      const days = rate['delivery_days'];

      if (!days || days.length === 0) {
        return '';
      }

      const min = days[0], max = days[days.length - 1];
      const label = min === max ? String(min) : `${min}–${max}`;

      return `<span class="cart-drawer__shipping-rate-days text-subdued text-xs">${this.strings.shippingEstimatorDeliveryEstimate.replace('@@', label)}</span>`;
    }

    async #showLoading(message) {
      const textElement = this.resultsElement.querySelector('.cart-drawer__shipping-loading-text');

      if (textElement) {
        return this.#swapLoadingText(textElement, message);
      }

      this.loadingShownAt = Date.now();
      this.resultsElement.innerHTML = `
        <div class="cart-drawer__shipping-loading">
          <span class="cart-drawer__shipping-spinner"></span>
          <span class="cart-drawer__shipping-loading-text text-subdued">${message}</span>
        </div>
      `;
      this.resultsElement.hidden = false;
    }

    async #swapLoadingText(textElement, message) {
      const elapsed = Date.now() - (this.loadingShownAt || 0);

      if (elapsed < 700) {
        await new Promise((resolve) => setTimeout(resolve, 700 - elapsed));
      }

      await textElement.animate(
        { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-0.25rem)'] },
        { duration: 150, easing: 'ease-in', fill: 'forwards' }
      ).finished;

      textElement.textContent = message;

      textElement.animate(
        { opacity: [0, 1], transform: ['translateY(0.25rem)', 'translateY(0)'] },
        { duration: 200, easing: 'ease-out', fill: 'forwards' }
      );
    }

    #showMessage(message) {
      this.resultsElement.innerHTML = `<p class="text-subdued">${message}</p>`;
      this.resultsElement.hidden = false;
    }

    #showRates(rates, location) {
      if (!rates || rates.length === 0) {
        this.#showMessage(this.strings.shippingEstimatorNoResults);
        return;
      }

      const header = `<p class="cart-drawer__shipping-results-heading text-subdued">${this.strings.shippingEstimatorResultsFor.replace('@@', location)}</p>`;

      this.resultsElement.innerHTML = header + rates.map((rate) => `
        <div class="cart-drawer__shipping-rate">
          <span class="cart-drawer__shipping-rate-info">
            <span>${rate['presentment_name']}</span>
            ${this.#formatDeliveryDays(rate)}
          </span>
          <span class="cart-drawer__shipping-rate-price">${this.#formatPrice(rate)}</span>
        </div>
      `).join('');
      this.resultsElement.hidden = false;

      this.resultsElement.animate(
        { opacity: [0, 1], transform: ['translateY(0.25rem)', 'translateY(0)'] },
        { duration: 250, easing: 'ease-out' }
      );
    }
  });
}
